import { useTranslation } from "react-i18next"
import { LoaderCircle, RefreshCcwIcon } from "lucide-react"

import { useModal } from "@/components/core/Root/modal-root"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/toastHook"

import { DialogEditUser } from "../edit/dialog-edit-user"
import { DialogResendPassword } from "../edit/dialog-resend-password"
import {
  useFetchUsersList,
  useMutationSyncUsers,
  useRevalidateUserList,
} from "../users-api"
import { UsersTable } from "./users-table"

export function UsersListPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { showModal } = useModal()
  const syncMutation = useMutationSyncUsers()
  const revalidate = useRevalidateUserList()

  const { data, isFetching, isLoading } = useFetchUsersList()
  const users = data?.data.agents ?? []

  const handleEditUser = async (cognitoUserId: string) => {
    const user = users.find((user) => user.cognito_user_id === cognitoUserId)
    if (!user) return
    const ok = await showModal((close) => (
      <DialogEditUser close={close} user={user} />
    ))
    if (ok) await revalidate() // by revalidating the user list AFTER the user closes the modal, there is a higher probability of the user list being up to date!
  }
  const handleResendPassword = async (cognitoUserId: string) => {
    const user = users.find((user) => user.cognito_user_id === cognitoUserId)
    if (!user) return
    const ok = await showModal((close) => (
      <DialogResendPassword close={close} user={user} />
    ))
    if (ok) await revalidate()
  }

  const handleSyncAgents = async () => {
    try {
      syncMutation.mutate()
    } catch {
      toast.error(t("syncAgents.error"))
    }
  }

  return (
    <main className="flex h-full flex-col gap-y-4 px-8 pt-10">
      <div className="container flex flex-col gap-8">
        <h1 className="font-h2-bold text-neutral-700">
          {t("sidebarMenu.users")}
          {users.length > 0 && <ShowUserTotal total={users.length} />}
        </h1>
        <UsersTable
          users={users}
          isLoading={isLoading || isFetching}
          actions={
            <Button
              onClick={handleSyncAgents}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <LoaderCircle className="animate-spin" />
                  {t("syncAgents.syncing")}
                </>
              ) : (
                <>
                  <RefreshCcwIcon />
                  {t("syncAgents.buttonText")}
                </>
              )}
            </Button>
          }
          onEdit={handleEditUser}
          onResendPassword={handleResendPassword}
        />
      </div>
    </main>
  )
}

function ShowUserTotal({ total }: { total: number }) {
  return <span className="ml-2 text-base text-neutral-500">({total})</span>
}
